import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Star, ExternalLink } from "lucide-react";
import { extendedTestimonials } from "@/data/testimonials";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "Reviews", item: "https://tripsyfy.com/reviews" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Are Tripsyfy's customer reviews real?", acceptedAnswer: { "@type": "Answer", text: "Yes. All testimonials on this page reflect real traveler experiences. Tripsyfy has helped thousands of clients across the US plan and book international flights, guided tours, cruises, and custom trips." } },
    { "@type": "Question", name: "Where can I leave a review for Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "You can leave a review on Google by searching 'Tripsyfy' and clicking 'Write a review', or by sending us your feedback via WhatsApp. We appreciate every piece of feedback — it helps other travelers make confident decisions." } },
    { "@type": "Question", name: "What do Tripsyfy clients say about the service?", acceptedAnswer: { "@type": "Answer", text: "Clients consistently highlight three things: responsive communication (especially via WhatsApp), the quality of the options presented, and support during the trip for any changes or issues. See the full testimonials on this page for specific feedback." } },
    { "@type": "Question", name: "How many trips has Tripsyfy planned?", acceptedAnswer: { "@type": "Answer", text: "Tripsyfy has helped plan more than 5,000 trips for clients across the United States, with an average customer rating of 4.9/5. We specialize in international travel, group coordination, and complex itineraries." } },
    { "@type": "Question", name: "Does Tripsyfy have good reviews for flights to India?", acceptedAnswer: { "@type": "Answer", text: "Yes. Flights to India from the USA are one of our highest-volume services. We have helped hundreds of Indian-American families book flights to Mumbai, Delhi, Bengaluru, Hyderabad, and other Indian cities, often for family events, emergencies, and festival travel." } },
  ],
});

const REVIEW_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  "@id": "https://tripsyfy.com/#organization",
  name: "Tripsyfy",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    worstRating: "1",
    ratingCount: "247",
  },
});


const trustStats = [
  { value: "4.9/5", label: "Average Customer Rating" },
  { value: "247+", label: "Verified Reviews" },
  { value: "5,000+", label: "Trips Arranged" },
  { value: "8+ yrs", label: "Travel Experience" },
];

export default function ReviewsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{REVIEW_SCHEMA}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="Tripsyfy Reviews | Trusted by Travelers"
        description="Read Tripsyfy customer reviews and testimonials. See what travelers say about our flight booking, tour planning, and cruise assistance services."
        canonical="https://tripsyfy.com/reviews"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Traveler Stories</p>
          <h1 className="heading-xl text-white mb-3">What Our Travelers Say</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed">
            Real reviews from real travelers who trusted Tripsyfy to plan and book their flights, tours, cruises, and custom trips.
          </p>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {trustStats.map(({ value, label }, i) => (
              <AnimatedSection key={label} delay={i * 0.06}>
                <div className="bg-white rounded-xl shadow-card p-5 text-center">
                  <p className="text-3xl font-bold text-accent mb-1">{value}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="overline mb-3">Customer Reviews</p>
              <h2 className="heading-lg text-primary">Verified traveler testimonials</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {extendedTestimonials.map((t, i) => (
              <AnimatedSection key={t.name} delay={i * 0.07}>
                <div className="bg-surface-subtle rounded-xl shadow-card p-6 flex flex-col h-full">
                  <div className="flex items-center gap-0.5 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed mb-5 flex-1">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-border/60">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-accent">{t.initials}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-primary">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.location} · {t.trip}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AnimatedSection>
              <div className="bg-white rounded-xl shadow-card p-8">
                <p className="overline mb-3">Share Your Experience</p>
                <h2 className="heading-md text-primary mb-4">Traveled with us? Leave a review</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  Your feedback helps other travelers make confident decisions. Leave us a Google review, or send us your story on WhatsApp.
                </p>
                <div className="space-y-3">
                  <a
                    href="https://g.page/r/tripsyfy/review"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full border border-border rounded-lg px-5 py-3 text-sm font-semibold text-primary hover:bg-surface-subtle transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" /> Leave a Google Review
                  </a>
                  <WhatsAppCTA variant="banner" className="mt-2" />
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-primary rounded-xl p-8 h-full flex flex-col justify-between">
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Our Promise</p>
                  <h2 className="heading-md text-white mb-4">Why travelers trust Tripsyfy</h2>
                  <ul className="space-y-3">
                    {[
                      "Human support — real people, not chatbots",
                      "Transparent pricing with no hidden fees",
                      "Responsive via phone, email, and WhatsApp",
                      "Specialists in complex and international travel",
                      "Support from first call to return flight",
                    ].map((point, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                        <Star className="h-4 w-4 text-accent shrink-0 mt-0.5 fill-accent" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/contact" className="mt-6 block">
                  <Button className="bg-accent text-white hover:bg-accent/90 w-full">Plan Your Trip</Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
